/**
 * NoiseTexture2D build: the size ceilings, and the `THREE.DataTexture` around the
 * pixels `pixels.ts` computes.
 */
import * as THREE from 'three';
import { IMAGE_MAX_PIXELS } from '../../../godot/project/image.js';
import { MAX_TEXTURE_EXTENT } from '../../../r3f/webglLimits.js';
import { seamlessSource } from '../../noise/image/noiseImage';
import { colourDataTexture } from '../colourDataTexture';
import { carryMipChain, GODOT_DEFAULT_MIN_FILTER } from '../texelTexture';
/**
 * Whether the previewer rasterises this texture. It refuses an axis past the WebGL ceiling it
 * assumes (`MAX_TEXTURE_EXTENT`), where Godot asks the device. Separately, Godot never builds a
 * seamless source past `Image::MAX_PIXELS` (`noise.cpp:43`, `image.cpp:2421`).
 */
export function noiseTextureFits({ width, height, seamless, seamlessBlendSkirt, }) {
    if (width > MAX_TEXTURE_EXTENT || height > MAX_TEXTURE_EXTENT)
        return false;
    if (!seamless)
        return true;
    return (seamlessSource(width, seamlessBlendSkirt) * seamlessSource(height, seamlessBlendSkirt) <= IMAGE_MAX_PIXELS);
}
/**
 * Bottom-up `levels`, computed from `input`, as the texture Godot builds from it. Several levels
 * are its mip chain, which the default sampler (`LINEAR_WITH_MIPMAPS`, `material.h:573`)
 * minifies between.
 */
export function noiseDataTexture(levels, input) {
    const { tex } = input;
    const texture = colourDataTexture(levels[0].data, tex.width, tex.height, imageFormat(input));
    carryMipChain(texture, levels, GODOT_DEFAULT_MIN_FILTER);
    // A normal map carries directions, not colour, so it starts raw, as its normal slot reads it.
    if (tex.asNormalMap)
        texture.colorSpace = THREE.NoColorSpace;
    // A seamless image tiles. A non-seamless one clamps like every other procedural texture.
    const wrap = tex.seamless ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
    texture.wrapS = wrap;
    texture.wrapT = wrap;
    return texture;
}
/**
 * `full` without its chain, over the same bytes, for a NoiseTexture2D whose `generate_mipmaps` is
 * off. The default sampler then reads its base level alone, as Godot's does.
 */
export function baseNoiseTexture(full, input) {
    const { width, height } = full.image;
    return noiseDataTexture([{ data: full.image.data, width, height }], input);
}
/**
 * The format of Godot's image: L8 from the noise (`noise.cpp:134`, `:157`), RGBA8 once the colour
 * ramp (`noise_texture_2d.cpp:187`) or the bump pass (`image.cpp:4127`) rewrites it.
 */
function imageFormat({ tex, colorRamp }) {
    return colorRamp || tex.asNormalMap ? 'RGBA8' : 'Lum8';
}
//# sourceMappingURL=build.js.map